import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Lock, AlertCircle, Loader, Info, ArrowRight, Mail } from 'lucide-react'
import toast from 'react-hot-toast'
import { apiClient } from '@/utils/api'

export default function DemoLoginPage() {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()

  // SECURITY: Ensure this page only loads on demo subdomain
  React.useEffect(() => {
    const hostname = window.location.hostname
    if (!hostname.includes('demo.housepoints.ai') && hostname !== 'localhost') {
      // Redirect non-demo families to their regular login
      window.location.href = '/login'
    }
  }, [])

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!username || !password) {
      toast.error('Please enter username and password')
      return
    }

    setLoading(true)

    try {
      const response = await apiClient.post('/api/auth/login', {
        username: username.toLowerCase(),
        password
      })

      if (response.token) {
        localStorage.setItem('auth_token', response.token)

        // Fetch full user data including points, level, streak, etc.
        const fullUser = await apiClient.getCurrentUser()
        localStorage.setItem('user', JSON.stringify(fullUser))

        // Set demo mode flag
        localStorage.setItem('demo_mode', 'true')

        toast.success('Login successful!')
        navigate('/dashboard')
      }
    } catch (error: any) {
      console.error('Login error:', error)
      toast.error(error.response?.data?.error || 'Invalid username or password')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="card text-center mb-6">
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
              <Lock className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-bold gradient-text mb-3">
            HousePoints Demo Login
          </h1>
          <p className="text-gray-600">
            Experience the full power of HousePoints
          </p>
        </div>

        {/* Demo Access Request Info */}
        <div className="card bg-gradient-to-br from-blue-50 to-purple-50 border-blue-200 mb-6">
          <div className="flex items-start space-x-3 mb-4">
            <Mail className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-blue-900 mb-2">Demo Access Required</h3>
              <p className="text-sm text-blue-800 mb-3">
                Your demo credentials have been sent to your email. Check your inbox for:
              </p>
              <ul className="text-sm text-blue-800 space-y-1 ml-4">
                <li>• Available usernames (parent and child accounts)</li>
                <li>• Your demo password</li>
                <li>• Quick start guide</li>
              </ul>
            </div>
          </div>

          <div className="bg-white/70 border border-blue-200 rounded-lg p-3 mt-3">
            <p className="text-xs font-semibold text-blue-900 uppercase tracking-wide mb-2">Account Types Available</p>
            <div className="grid grid-cols-2 gap-2 text-xs text-gray-700">
              <div>
                <span className="font-medium">Parent Accounts:</span>
                <p className="text-gray-600">Full admin access to all features</p>
              </div>
              <div>
                <span className="font-medium">Child Accounts:</span>
                <p className="text-gray-600">Age-appropriate limited access</p>
              </div>
            </div>
          </div>
        </div>

        {/* Login Form */}
        <div className="card">
          <h2 className="text-xl font-semibold mb-4">Enter Your Credentials</h2>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                Username
              </label>
              <input
                type="text"
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter username from email"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={loading}
                autoFocus
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password from email"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn btn-primary flex items-center justify-center space-x-2"
            >
              {loading ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  <span>Logging in...</span>
                </>
              ) : (
                <>
                  <span>Login to Demo</span>
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </form>
        </div>

        {/* Demo Info */}
        <div className="card bg-yellow-50 border-yellow-200 mt-6">
          <div className="flex items-start space-x-3">
            <Info className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-yellow-900 mb-2">Demo Mode Information</h3>
              <ul className="text-sm text-yellow-800 space-y-1">
                <li>• Your approval email includes multiple account options (parent and child perspectives)</li>
                <li>• Demo data is shared across all users and resets nightly at midnight EST</li>
                <li>• Some actions are view-only to preserve the demo experience for everyone</li>
                <li>• Try different accounts to see how parents and children interact with the system</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center space-y-4">
          <div className="card bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-300">
            <p className="text-base font-semibold text-gray-900 mb-2">
              Don't have demo access yet?
            </p>
            <a
              href="https://housepoints.ai/demo/signup"
              className="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
            >
              Request Demo Access
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
            <p className="text-xs text-gray-600 mt-3">
              Credentials will be emailed to you within 24 hours
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
